import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { apartmentInfo } from '../data/mock';

// Reviews data from Booking.com
const reviews = [
  {
    id: 1,
    name: 'Anejae',
    country: 'Morocco',
    countryCode: 'MA',
    text: {
      en: "The apartment is very clean, spacious and well equipped, the bed is so comfortable, the bathroom is so nice, everything is awesome, it is in a very nice location in center close to everything, the hosts were so friendly, welcoming and helpful, I highly recommend this apartment.",
      hr: "Apartman je jako čist, prostran i dobro opremljen, krevet je tako udoban, kupaonica je tako lijepa, sve je super, na prekrasnoj lokaciji u centru blizu svega, domaćini su bili tako ljubazni, gostoljubivi i od pomoći. Toplo preporučujem ovaj apartman."
    },
    rating: 10,
    date: 'May 2026',
    title: {
      en: "A wonderful place to stay in, I highly recommend.",
      hr: "Prekrasno mjesto za boravak, toplo preporučujem."
    }
  },
  {
    id: 2,
    name: 'Snežana',
    country: 'Canada',
    countryCode: 'CA',
    text: {
      en: "I liked everything about this property. It is very clean and comfortable. Location is great, walking distance to most tourist attractions in the area. The hosts are very nice, accommodating, welcoming, helpful, and very quick in response to any inquiry. We will be returning next time we visit the city!",
      hr: "Svidjelo mi se sve u vezi ovog smještaja. Vrlo je čist i udoban. Lokacija je odlična, na pješačkoj udaljenosti od većine turističkih atrakcija. Domaćini su jako ljubazni, susretljivi, gostoljubivi i brzo odgovaraju na svaki upit. Vraćamo se sljedeći put!"
    },
    rating: 10,
    date: 'August 2026',
    title: {
      en: "Exceptional",
      hr: "Izvanredno"
    }
  },
  {
    id: 3,
    name: 'Stevan',
    country: 'Serbia',
    countryCode: 'RS',
    text: {
      en: "Clean, close to the city center, apartment stacked with snacks, fruits, drinks. The host was very welcoming and helpful.",
      hr: "Čisto, blizu centra grada, apartman pun grickalica, voća, pića. Domaćin je bio jako gostoljubiv i od pomoći."
    },
    rating: 10,
    date: 'May 2026',
    title: {
      en: "Amazing place",
      hr: "Nevjerojatno mjesto"
    }
  },
  {
    id: 4,
    name: 'Giulia',
    country: 'Italy',
    countryCode: 'IT',
    text: {
      en: "The apartment was beautiful and clean. We really enjoyed the kindness of the hosts.",
      hr: "Apartman je bio prekrasan i čist. Jako smo uživali u ljubaznosti domaćina."
    },
    rating: 10,
    date: 'July 2026',
    title: {
      en: "Exceptional",
      hr: "Izvanredno"
    }
  },
  {
    id: 5,
    name: 'Jasmin',
    country: 'Germany',
    countryCode: 'DE',
    text: {
      en: "Everything was amazing! The hosts are very kind people. It was clean, there was nothing missing, high quality furnishing. Cannot recommend this apartment enough.",
      hr: "Sve je bilo nevjerojatno! Domaćini su jako ljubazni ljudi. Bilo je čisto, ništa nije nedostajalo, visokokvalitetan namještaj. Ne mogu dovoljno preporučiti ovaj apartman."
    },
    rating: 10,
    date: 'July 2026',
    title: {
      en: "The best apartment to stay in Sarajevo",
      hr: "Najbolji apartman za boravak u Sarajevu"
    }
  },
  {
    id: 6,
    name: 'Snježana',
    country: 'Germany',
    countryCode: 'DE',
    text: {
      en: "The apartment is near Baščaršija, top location... walk down the street to the right, cross the traffic light and turn left through Ferhadija - you reach Čaršija in seven minutes for Bosnian coffee and the best ćevapi. We came for a Dino Merlin concert and the apartment was in the best possible location - we were at Koševo stadium in 15 minutes on foot.",
      hr: "Apartman je u blizini Baščaršije, top lokacija... spustite se ulicom desno dolje, prijeđete semafor i na lijevo kroz Ferhadiju - dođete do Čaršije za sedam minuta na bosansku kafu i najbolje ćevape. Mi smo došli na koncert Dino Merlina i apartman je bio na najboljoj mogućoj lokaciji - za 15 minuta smo bili pješke na stadionu Koševo."
    },
    rating: 10,
    date: 'August 2026',
    title: {
      en: "Exceptional",
      hr: "Izvanredno"
    }
  }
];

// Country flag component
const CountryFlag = ({ countryCode }) => {
  const flagUrl = `https://t-cf.bstatic.com/design-assets/assets/v3.202.0/images-flags/${countryCode.charAt(0).toUpperCase() + countryCode.slice(1).toLowerCase()}.png`;
  return (
    <img 
      src={flagUrl} 
      alt={countryCode}
      className="w-5 h-4 inline-block rounded-sm"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
};

const ReviewCard = ({ review, language }) => {
  const text = review.text[language] || review.text.en;
  const title = review.title[language] || review.title.en;
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-lg">
            {review.name.charAt(0)}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{review.name}</p>
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <CountryFlag countryCode={review.countryCode} />
              <span>{review.country}</span>
            </div>
          </div>
        </div>
        
        {/* Rating Badge - Booking.com style */}
        <div className="bg-[#003580] text-white px-3 py-1.5 rounded-tl-lg rounded-tr-lg rounded-br-lg font-bold text-lg">
          {review.rating}
        </div>
      </div>
      
      {/* Title */}
      <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
      
      {/* Quote */}
      <div className="relative flex-grow">
        <Quote size={24} className="absolute -top-1 -left-1 text-amber-200 rotate-180" />
        <p className="text-gray-600 text-sm leading-relaxed pl-6 pr-2">
          {text}
        </p>
      </div>
      
      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">{review.date}</span>
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={12} className="text-amber-400 fill-amber-400" />
          ))}
        </div>
      </div>
    </div>
  );
};

const Reviews = () => {
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Show 3 reviews at a time on desktop, 1 on mobile
  const reviewsPerPage = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3;
  const maxIndex = Math.ceil(reviews.length / reviewsPerPage) - 1;
  
  const nextReviews = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };
  
  const prevReviews = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };
  
  const visibleReviews = reviews.slice(
    currentIndex * reviewsPerPage, 
    (currentIndex * reviewsPerPage) + reviewsPerPage
  );

  const sectionText = {
    en: {
      subtitle: 'Guest Reviews',
      title: 'What Our Guests Say',
      description: 'Read verified reviews from guests who stayed at our apartment',
      viewAll: 'View all reviews on Booking.com',
      overallRating: 'Overall Rating',
      basedOn: 'Based on 29 verified reviews'
    },
    hr: {
      subtitle: 'Recenzije gostiju',
      title: 'Što kažu naši gosti',
      description: 'Pročitajte verificirane recenzije gostiju koji su boravili u našem apartmanu',
      viewAll: 'Pogledajte sve recenzije na Booking.com',
      overallRating: 'Ukupna ocjena',
      basedOn: 'Na temelju 29 verificiranih recenzija'
    }
  };
  
  const txt = sectionText[language] || sectionText.en;

  return (
    <section id="recenzije" className="py-20 bg-amber-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-amber-700 text-sm font-medium tracking-wider mb-2">{txt.subtitle}</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
            {txt.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            {txt.description}
          </p>
          
          {/* Overall Rating Badge */}
          <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-6 py-4 shadow-lg">
            <div className="text-center">
              <div className="bg-[#003580] text-white text-3xl font-bold px-4 py-2 rounded-tl-xl rounded-tr-xl rounded-br-xl">
                10
              </div>
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900">{txt.overallRating}</p>
              <p className="text-sm text-gray-500">{txt.basedOn}</p>
              <div className="flex items-center gap-1 mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={prevReviews}
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-amber-50 transition-colors hidden md:block"
            aria-label="Previous reviews"
          >
            <ChevronLeft size={24} className="text-gray-600" />
          </button>
          
          <button
            onClick={nextReviews}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-amber-50 transition-colors hidden md:block"
            aria-label="Next reviews"
          >
            <ChevronRight size={24} className="text-gray-600" />
          </button>

          {/* Reviews Grid */}
          <div className="grid md:grid-cols-3 gap-6 transition-all duration-500">
            {visibleReviews.map((review) => (
              <ReviewCard key={review.id} review={review} language={language} />
            ))}
          </div>
          
          {/* Mobile Navigation */}
          <div className="flex justify-center gap-2 mt-6 md:hidden">
            {[...Array(maxIndex + 1)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'bg-amber-700 w-6' : 'bg-amber-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Dots Navigation - Desktop */}
        <div className="hidden md:flex justify-center gap-2 mt-8">
          {[...Array(maxIndex + 1)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === currentIndex ? 'bg-amber-700 w-8' : 'bg-amber-300 hover:bg-amber-400'
              }`}
            />
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-10">
          <a
            href={apartmentInfo.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#003580] hover:bg-[#00264d] text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <img 
              src="https://cf.bstatic.com/static/img/favicon/9ca83ba2a5a3293ff07452cb24949a5843af4592.svg" 
              alt="Booking.com" 
              className="w-5 h-5"
            />
            {txt.viewAll}
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
